import { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';

/** Данные из формы создания игрока — ровно то, по чему ищем совпадение в составе. */
export interface SquadCandidate {
  /** Id уже заведённого игрока, если создание идёт от карточки существующего. */
  userId?: number;
  name: string;
  surname: string;
  middleName?: string;
  /** Как в форме — `dd.MM.yyyy`; из состава приходит ISO. Оба вида понимает `normalizeBirthDate`. */
  birthDate?: string;
}

/** «Иванов» и « иванов » — один человек; «ё» и «е» пользователи путают постоянно. */
const normalizeName = (value?: string): string => (value ?? '').trim().toLowerCase().replace(/ё/g, 'е');

/**
 * Приводит дату к `yyyy-MM-dd`. Форма отдаёт `dd.MM.yyyy`, состав команды — ISO.
 * ISO режем строкой, а не через `new Date()`: разбор с `Z` и последующий вывод в
 * локальной зоне сдвигает день в минусовых поясах.
 */
export function normalizeBirthDate(value?: string): string {
  const birthDate = (value ?? '').trim();
  if (!birthDate) {
    return '';
  }

  const asFormValue = birthDate.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (asFormValue) {
    const [, day, month, year] = asFormValue;
    return `${year}-${month}-${day}`;
  }

  return birthDate.slice(0, 10);
}

/**
 * Ищет в составе команды того же игрока: сначала по id (создание от карточки
 * существующего игрока), иначе по полному ФИО и дате рождения.
 *
 * Сравнение намеренно строгое — совпасть должно всё, включая отчество и дату.
 * Однофамильцы одного возраста в командах встречаются, и заблокировать создание
 * реального игрока хуже, чем лишний раз сходить на бэкенд: проверка на стороне
 * сервера никуда не делась и остаётся страховкой (IMGM-1438).
 */
export function findPlayerInSquad(players: TeamPlayer[] = [], candidate: SquadCandidate): TeamPlayer | undefined {
  if (candidate.userId) {
    return players.find((player) => player.userId === candidate.userId);
  }

  const birthDate = normalizeBirthDate(candidate.birthDate);
  if (!birthDate) {
    return undefined;
  }

  const surname = normalizeName(candidate.surname);
  const name = normalizeName(candidate.name);
  const middleName = normalizeName(candidate.middleName);

  return players.find(
    (player) =>
      normalizeName(player.lastName) === surname &&
      normalizeName(player.firstName) === name &&
      normalizeName(player.middleName) === middleName &&
      normalizeBirthDate(player.birthDate) === birthDate,
  );
}
